import type { AdminLearningContentRow, ProgramFixedContentRowUpsert } from '../types/domain';
import {
  formatLearningTextForDisplay,
  getUnitTextAndNoteForDisplay,
  parseMemoFieldStorageDeep,
} from './programCellDisplayText';

const CSV_HEADERS = [
  '편제',
  '순번',
  '단원',
  '성취기준',
  '학습내용',
  '쪽수',
  '보조쪽수',
  '차시',
  '누계',
  '준비물',
  '범교과',
  '메모',
  '평가',
] as const;

const HEADER_INDEX_BY_ALIAS: Record<string, keyof CsvProgramRow> = {
  편제: 'track_text',
  시간표: 'track_text',
  시간표명: 'track_text',
  seq: 'seq_text',
  순번: 'seq_text',
  단원: 'unit_text',
  성취기준: 'achievement_text',
  학습내용: 'learning_text',
  쪽수: 'page_text',
  보조쪽수: 'assistant_page_text',
  차시: 'target_lesson_text',
  누계: 'total_lesson_text',
  준비물: 'material_text',
  범교과: 'record_cross_curricular_text',
  메모: 'record_memo_text',
  평가: 'record_evaluation_text',
};

type CsvProgramRow = {
  track_text: string;
  seq_text: string;
  unit_text: string;
  achievement_text: string;
  learning_text: string;
  page_text: string;
  assistant_page_text: string;
  target_lesson_text: string;
  total_lesson_text: string;
  material_text: string;
  record_cross_curricular_text: string;
  record_memo_text: string;
  record_evaluation_text: string;
};

const EMPTY_CSV_PROGRAM_ROW: CsvProgramRow = {
  track_text: '',
  seq_text: '',
  unit_text: '',
  achievement_text: '',
  learning_text: '',
  page_text: '',
  assistant_page_text: '',
  target_lesson_text: '',
  total_lesson_text: '',
  material_text: '',
  record_cross_curricular_text: '',
  record_memo_text: '',
  record_evaluation_text: '',
};

function escapeCsvCell(value: string): string {
  const normalized = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (normalized.includes('"') || normalized.includes(',') || normalized.includes('\n')) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

function parseDelimited(text: string, delimiter: ',' | '\t'): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  const raw = text.replace(/^\uFEFF/, '');
  const pushCell = () => {
    row.push(cell);
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    if (row.some((value) => value.trim().length > 0)) out.push(row);
    row = [];
  };
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i] ?? '';
    const next = raw[i + 1] ?? '';
    if (ch === '"') {
      const atCellStart = cell.length === 0;
      if (!inQuotes && !atCellStart) {
        cell += ch;
        continue;
      }
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      pushCell();
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && next === '\n') i += 1;
      pushRow();
      continue;
    }
    cell += ch;
  }
  if (cell.length > 0 || row.length > 0) pushRow();
  return out;
}

function rowHasAnyProgramData(row: CsvProgramRow): boolean {
  return (
    row.unit_text.trim().length > 0 ||
    row.achievement_text.trim().length > 0 ||
    row.learning_text.trim().length > 0 ||
    row.page_text.trim().length > 0 ||
    row.assistant_page_text.trim().length > 0 ||
    row.target_lesson_text.trim().length > 0 ||
    row.total_lesson_text.trim().length > 0 ||
    row.material_text.trim().length > 0 ||
    row.record_cross_curricular_text.trim().length > 0 ||
    row.record_memo_text.trim().length > 0 ||
    row.record_evaluation_text.trim().length > 0
  );
}

/**
 * CSV 1셀에 메모(text/note)를 보존할 수 있도록:
 * - note가 있으면 JSON 문자열({"text","note"})로 저장
 * - note가 없으면 기존처럼 plain text
 */
function serializeMemoCellForCsv(raw: string): string {
  const parsed = parseMemoFieldStorageDeep(String(raw ?? ''));
  if (parsed.note.trim()) {
    const payload: { text: string; note: string } = {
      text: parsed.text,
      note: parsed.note,
    };
    return JSON.stringify(payload);
  }
  return parsed.text;
}

function normalizeMemoCellFromCsv(raw: string): string {
  const parsed = parseMemoFieldStorageDeep(String(raw ?? ''));
  if (!parsed.text.trim() && !parsed.note.trim()) return '';
  if (!parsed.note.trim()) return parsed.text;
  return JSON.stringify({ text: parsed.text, note: parsed.note });
}

function normalizeHeaderRow(values: string[]): string[] {
  return values.map((value) => value.trim().replace(/\s+/g, ''));
}

function looksLikeHeader(values: string[]): boolean {
  const normalized = normalizeHeaderRow(values);
  return normalized.some((value) => value in HEADER_INDEX_BY_ALIAS);
}

export function createProgramLearningContentCsv(rows: ProgramFixedContentRowUpsert[]): string {
  const bodyRows = rows
    .map((row, idx) => {
      const unit = getUnitTextAndNoteForDisplay(String(row.unit_text ?? ''));
      const cross = serializeMemoCellForCsv(String(row.record_cross_curricular_text ?? ''));
      const memo = serializeMemoCellForCsv(String(row.record_memo_text ?? ''));
      const evalText = serializeMemoCellForCsv(String(row.record_evaluation_text ?? ''));
      const csvRow: CsvProgramRow = {
        track_text: String(row.timetable_text ?? '').trim(),
        seq_text: String(idx + 1),
        unit_text: unit.text,
        achievement_text: unit.note,
        learning_text: serializeMemoCellForCsv(String(row.learning_text ?? '')),
        page_text: String(row.page_text ?? '').trim(),
        assistant_page_text: String(row.assistant_page_text ?? '').trim(),
        target_lesson_text: String(row.current_period_text ?? '').trim(),
        total_lesson_text: String(row.total_period_text ?? '').trim(),
        material_text: String(row.material_text ?? '').trim(),
        record_cross_curricular_text: cross,
        record_memo_text: memo,
        record_evaluation_text: evalText,
      };
      return csvRow;
    })
    .filter(rowHasAnyProgramData);

  if (bodyRows.length === 0) {
    throw new Error('내보낼 학습내용(단원~평가)이 없습니다.');
  }

  const headerLine = CSV_HEADERS.join(',');
  const dataLines = bodyRows.map((row) =>
    [
      row.track_text,
      row.seq_text,
      row.unit_text,
      row.achievement_text,
      row.learning_text,
      row.page_text,
      row.assistant_page_text,
      row.target_lesson_text,
      row.total_lesson_text,
      row.material_text,
      row.record_cross_curricular_text,
      row.record_memo_text,
      row.record_evaluation_text,
    ]
      .map((value) => escapeCsvCell(value))
      .join(',')
  );
  return `\uFEFF${[headerLine, ...dataLines].join('\n')}`;
}

function rowFromHeaderMap(values: string[], headerMap: Array<keyof CsvProgramRow | null>): CsvProgramRow {
  const next: CsvProgramRow = { ...EMPTY_CSV_PROGRAM_ROW };
  headerMap.forEach((key, idx) => {
    if (!key) return;
    next[key] = String(values[idx] ?? '').trim();
  });
  return next;
}

function rowFromPositional(values: string[]): CsvProgramRow {
  const cols = [...values];
  while (cols.length < 13) cols.push('');
  return {
    track_text: cols[0]?.trim() ?? '',
    seq_text: cols[1]?.trim() ?? '',
    unit_text: cols[2]?.trim() ?? '',
    achievement_text: cols[3]?.trim() ?? '',
    learning_text: cols[4]?.trim() ?? '',
    page_text: cols[5]?.trim() ?? '',
    assistant_page_text: cols[6]?.trim() ?? '',
    target_lesson_text: cols[7]?.trim() ?? '',
    total_lesson_text: cols[8]?.trim() ?? '',
    material_text: cols[9]?.trim() ?? '',
    record_cross_curricular_text: cols[10]?.trim() ?? '',
    record_memo_text: cols[11]?.trim() ?? '',
    record_evaluation_text: cols[12]?.trim() ?? '',
  };
}

export function parseProgramLearningContentCsv(
  text: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: 'term1' | 'term2'
): AdminLearningContentRow[] {
  const source = text.replace(/^\uFEFF/, '');
  if (!source.trim()) return [];
  const firstLine = source.split(/\r\n|\n|\r/, 1)[0] ?? '';
  const commaCount = (firstLine.match(/,/g) ?? []).length;
  const tabCount = (firstLine.match(/\t/g) ?? []).length;
  const delimiter: ',' | '\t' = tabCount > commaCount ? '\t' : ',';
  const rows = parseDelimited(source, delimiter);
  if (rows.length === 0) return [];

  const first = rows[0] ?? [];
  const useHeader = looksLikeHeader(first);
  const headerMap = useHeader
    ? normalizeHeaderRow(first).map((header) => HEADER_INDEX_BY_ALIAS[header] ?? null)
    : [];
  const dataRows = (useHeader ? rows.slice(1) : rows)
    .map((values) => (useHeader ? rowFromHeaderMap(values, headerMap) : rowFromPositional(values)))
    .filter(rowHasAnyProgramData);

  return dataRows.map((row, index) => ({
    id: `csv-${Date.now()}-${index}`,
    workspace_id: null,
    grade_number: gradeNumber,
    sub_category: subCategory,
    template_name: 'CSV 가져오기',
    order_index: index,
    publisher: 'CSV',
    track_text: row.track_text,
    seq_text: row.seq_text || String(index + 1),
    unit_text: row.unit_text,
    achievement_text: row.achievement_text,
    learning_text: normalizeMemoCellFromCsv(row.learning_text),
    page_text: row.page_text,
    assistant_page_text: row.assistant_page_text,
    target_lesson_text: row.target_lesson_text,
    total_lesson_text: row.total_lesson_text,
    material_text: row.material_text,
    record_cross_curricular_text: normalizeMemoCellFromCsv(row.record_cross_curricular_text),
    record_memo_text: normalizeMemoCellFromCsv(row.record_memo_text),
    record_evaluation_text: normalizeMemoCellFromCsv(row.record_evaluation_text),
  }));
}
